import React from 'react';
import { motion } from 'framer-motion';
import { FiArrowLeft } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  // Scroll to top on component mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8">
            <FiArrowLeft className="mr-2" />
            Back to Home
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="glass-card p-8 md:p-12 rounded-2xl"
          >
            <h1 className="text-3xl md:text-4xl font-display font-bold mb-8">Privacy Policy</h1>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg">Last Updated: June 1, 2025</p>
              
              <h2>1. Introduction</h2>
              <p>
                Welcome to xdimension ("we," "our," or "us"). We are committed to protecting your privacy and personal data. 
                This Privacy Policy explains how we collect, use, process, and share your information when you visit our 
                website xdimension.ai, interact with us, or use our services.
              </p>

              <h2>2. Data Controller</h2>
              <p>
                xdimension AI Corp.<br />
                Website: xdimension.ai<br />
                Email: privacy@xdimension.ai
              </p>

              <h2>3. Information We Collect</h2>
              <h3>3.1 Information You Provide to Us</h3>
              <p>We collect information you provide directly to us when you:</p>
              <ul>
                <li>Fill out forms on our website (such as contact or pre-order forms)</li>
                <li>Correspond with us via email</li>
                <li>Subscribe to our newsletter</li>
                <li>Request customer support</li>
              </ul>
              <p>This information may include your name, email address, and any other information you choose to provide.</p>

              <h3>3.2 Information We Collect Automatically</h3>
              <p>When you access or use our website, we may automatically collect:</p>
              <ul>
                <li>
                  <strong>Log Information:</strong> Information about your use of our website, including your IP address, 
                  browser type, access times, pages viewed, and referring/exit pages.
                </li>
                <li>
                  <strong>Device Information:</strong> Information about the device used to access our website, including 
                  device type, operating system, and unique device identifiers.
                </li>
                <li>
                  <strong>Cookies and Similar Technologies:</strong> We use cookies and similar technologies to collect information 
                  about your browsing behavior and to remember your preferences. For more information, please see our 
                  <Link to="/cookies" className="text-primary-600"> Cookie Policy</Link>.
                </li>
              </ul>

              <h2>4. How We Use Your Information</h2>
              <p>We use the information we collect for various purposes, including to:</p>
              <ul>
                <li>Provide, maintain, and improve our website and services</li>
                <li>Process transactions and send related information</li>
                <li>Send you technical notices, updates, security alerts, and support messages</li>
                <li>Respond to your comments, questions, and requests</li>
                <li>Communicate with you about products, services, offers, and events offered by xdimension</li>
                <li>Monitor and analyze trends, usage, and activities in connection with our website</li>
                <li>Detect, investigate, and prevent fraudulent transactions and other illegal activities</li>
                <li>Personalize your experience on our website</li>
              </ul>

              <h2>5. Legal Basis for Processing (EU Users)</h2>
              <p>If you are a user from the European Economic Area (EEA), our legal basis for collecting and using your personal information depends on the specific information and the context in which we collect it. We typically process your personal data when:</p>
              <ul>
                <li>We need to perform a contract with you</li>
                <li>You have given us consent to do so</li>
                <li>The processing is in our legitimate interests and not overridden by your rights</li>
                <li>We need to comply with legal obligations</li>
              </ul>

              <h2>6. Data Sharing and Disclosure</h2>
              <p>We may share your personal information with:</p>
              <ul>
                <li>
                  <strong>Service Providers:</strong> Vendors, consultants, and other service providers who need access to such information 
                  to carry out work on our behalf.
                </li>
                <li>
                  <strong>Business Transfers:</strong> In connection with any merger, sale of company assets, financing, or acquisition of 
                  all or a portion of our business by another company.
                </li>
                <li>
                  <strong>Legal Requirements:</strong> When required by law or when we believe disclosure is necessary to protect our rights, 
                  property, or safety, or the rights, property, or safety of others.
                </li>
                <li>
                  <strong>With Your Consent:</strong> We may share information with third parties when you consent to such sharing.
                </li>
              </ul>

              <h2>7. International Data Transfers</h2>
              <p>
                Your personal data may be transferred to, and processed in, countries other than the country in which you are resident. 
                These countries may have data protection laws that are different from the laws of your country. We implement 
                appropriate safeguards to ensure that your personal information remains protected in accordance with this Privacy Policy.
              </p>

              <h2>8. Data Retention</h2>
              <p>
                We will retain your personal information only for as long as necessary to fulfill the purposes for which we collected it, 
                including for the purposes of satisfying any legal, accounting, or reporting requirements.
              </p>

              <h2>9. Your Rights</h2>
              <p>
                Depending on your location, you may have certain rights regarding your personal information, including:
              </p>
              <ul>
                <li>The right to access, update or delete the information we have about you</li>
                <li>The right of rectification - to have your information corrected if it is inaccurate or incomplete</li>
                <li>The right to object - to our processing of your personal information</li>
                <li>The right of restriction - to request that we restrict the processing of your personal information</li>
                <li>The right to data portability - to receive your personal information in a structured, commonly used, and machine-readable format</li>
                <li>The right to withdraw consent - to withdraw your consent at any time where we relied on your consent to process your personal information</li>
              </ul>

              <h3>9.1 European Users</h3>
              <p>
                If you are a resident of the European Economic Area (EEA), you have certain rights under the General Data Protection Regulation (GDPR). 
                To exercise these rights, please contact us at privacy@xdimension.ai.
              </p>

              <h3>9.2 California Residents</h3>
              <p>
                If you are a California resident, you have rights under the California Consumer Privacy Act (CCPA), including:
              </p>
              <ul>
                <li>The right to know what personal information we collect, use, disclose, and sell</li>
                <li>The right to delete personal information we collect or maintain</li>
                <li>The right to opt-out of the sale of personal information</li>
                <li>The right to non-discrimination for exercising your CCPA rights</li>
              </ul>
              <p>
                To exercise these rights, please contact us at privacy@xdimension.ai.
              </p>

              <h2>10. Children's Privacy</h2>
              <p>
                Our website is not directed to children under 16, and we do not knowingly collect personal information from children. 
                If you are a parent or guardian and believe your child has provided us with personal information, please contact us.
              </p>

              <h2>11. Security</h2>
              <p>
                We take reasonable measures to help protect your personal information from loss, theft, misuse, unauthorized access, 
                disclosure, alteration, and destruction. However, no internet or email transmission is ever fully secure or error-free.
              </p>

              <h2>12. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page 
                and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
              </p>

              <h2>13. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <p>
                xdimension AI<br />
                Website: xdimension.ai<br />
                Email: privacy@xdimension.ai
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;